import React from 'react';

import { IComponent } from '../../../typings/generic-component';
import useCountdown, { Duration } from '../../../hooks/useCountdown';
import { MatchStatusType } from '../../../typings/match';
import { makeClassNames } from '../../../utils/styles';

import { useAnimateCountdown, useShouldRenderMatchCountdown } from './match-countdown-hooks';
import './match-countdown.scss';

export interface IMatchCountdown extends IComponent {
  tick?: number;
  matchDate?: string;
  matchStatus: MatchStatusType;
  enabled?: boolean;
}

const formatSecAndMin = (num: number) => String(num >= 0 ? num : 0).padStart(2, '0');

const classNames = (animateCountdown: boolean, isPreMatch: boolean) =>
  makeClassNames({
    result: ['match-countdown__result'],
    countdown: ['match-countdown', isPreMatch && animateCountdown && 'match-countdown--animated'],
    countdownTime: ['match-countdown__time'],
  });

const MatchCountdown = ({ matchDate, tick = 1000, children, matchStatus, enabled = true }: IMatchCountdown) => {
  const [isBeforeMatch, isBeforeMatchTimeout] = useShouldRenderMatchCountdown({
    status: matchStatus,
    startDate: matchDate,
    enabled,
  });

  const { minutes, seconds, duration }: Duration = useCountdown(Date.parse(matchDate ?? ''), tick, isBeforeMatch);
  const [animateCountdown, isMatchStarted] = useAnimateCountdown(duration, isBeforeMatch);
  const cn = classNames(animateCountdown, matchStatus === 'PreMatch');

  const formattedMinutes = formatSecAndMin(minutes);
  const formattedSeconds = formatSecAndMin(seconds);

  return (
    <span className={cn.countdown}>
      {isBeforeMatchTimeout && !isMatchStarted ? (
        <span className={cn.countdownTime}>
          {`${formattedMinutes}:${formattedSeconds}`.split('').map((letter, index) => (
            <span key={index}>{letter}</span>
          ))}
        </span>
      ) : (
        <span className={cn.result}>{children}</span>
      )}
    </span>
  );
};

export default MatchCountdown;
